import { z } from "zod";
export const subscriptionResourceContextSchema = z.object({
    workspaceId: z.string().uuid(),
    subscriptionPlanId: z.string().uuid(),
});
// Core sends UTC timestamps without a zone designator; read them as UTC.
export const coreTimestampSchema = z
    .string()
    .transform((value) => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value) ? value : `${value}Z`)
    .pipe(z.string().datetime({ offset: true }));
export const billingPeriodSchema = z.enum(["monthly", "yearly"]);
export const billingProviderSchema = z.string().trim().min(1).max(64);
export const planPriceSchema = z.object({
    amount: z.number().nonnegative().finite(),
    priceId: z.string().trim().min(1).max(200).optional(),
    paymentOptionId: z.string().trim().min(1).max(200).optional(),
});
export const billingPlanSchema = z.object({
    id: z.string().trim().min(1).max(100),
    salesPackId: z.string().trim().min(1).max(200).optional(),
    productVariantId: z.string().trim().min(1).max(200).optional(),
    productVariantTitle: z.string().trim().min(1).max(200).optional(),
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1_000).default(""),
    currency: z
        .string()
        .trim()
        .length(3)
        .transform((value) => value.toUpperCase()),
    prices: z.object({
        monthly: planPriceSchema,
        yearly: planPriceSchema,
    }),
    features: z.array(z.string().trim().min(1).max(300)).max(50).default([]),
    popular: z.boolean().default(false),
});
export const billingPlanInputSchema = z.object({
    id: z.string().trim().min(1).max(100),
    salesPackId: z.string().trim().min(1).max(200).optional(),
    productVariantId: z.string().trim().min(1).max(200).optional(),
    productVariantTitle: z.string().trim().min(1).max(200).optional(),
    name: z.string().trim().min(1).max(200),
    description: z.string().trim().max(1_000).default(""),
    currency: z
        .string()
        .trim()
        .length(3)
        .transform((value) => value.toUpperCase())
        .optional(),
    prices: z.object({
        monthly: planPriceSchema.optional(),
        yearly: planPriceSchema.optional(),
    }),
    purchaseAvailable: z.boolean().default(true),
    unavailableReason: z.string().trim().min(1).max(300).optional(),
    features: z.array(z.string().trim().min(1).max(300)).max(50).default([]),
    popular: z.boolean().default(false),
});
export const billingPlansSchema = z.array(billingPlanSchema).max(20);
export const billingPlanInputsSchema = z.array(billingPlanInputSchema).max(20);
export const subscriptionSchema = z.object({
    id: z.string().min(1),
    planId: z.string().min(1),
    planName: z.string().trim().min(1).optional(),
    status: z.enum(["active", "trialing", "past_due", "paused", "canceled"]),
    billingPeriod: billingPeriodSchema,
    currentPeriodEnd: z.string().datetime().optional(),
    cancelAtPeriodEnd: z.boolean().default(false),
});
export const billingOverviewSchema = z.object({
    plans: z.array(z.object({
        id: z.string().uuid(),
        title: z.string().trim().min(1),
        status: z.number().int(),
        billingPeriod: z.number().int().nullable(),
        startDate: coreTimestampSchema.nullable(),
        nextPaymentDate: coreTimestampSchema.nullable(),
        nextPaymentAmount: z.number().finite().nullable(),
        currency: z.string().trim().length(3).nullable(),
        salesPack: z.string().trim().nullable(),
        trial: z
            .object({
            startedAt: coreTimestampSchema.nullable(),
            endsAt: coreTimestampSchema,
            amount: z.number().finite().nullable(),
        })
            .nullable()
            .default(null),
    })),
    subscriptions: z.array(z.object({
        id: z.string().uuid(),
        planId: z.string().uuid(),
        title: z.string().trim().min(1),
        kind: z.enum(["workspace", "add-on"]),
        active: z.boolean(),
        quantity: z.number().finite().nullable(),
        usedQuantity: z.number().finite().nullable(),
        startDate: coreTimestampSchema.nullable(),
        endDate: coreTimestampSchema.nullable(),
        productVariantId: z.string().uuid().nullable().default(null),
        productVariant: z.string().trim().nullable(),
        workspaceId: z.string().uuid().nullable().default(null),
        workspace: z.string().trim().nullable(),
    })),
    payments: z.array(z.object({
        id: z.string().uuid(),
        code: z.string().trim().nullable(),
        title: z.string().trim().nullable(),
        status: z.number().int(),
        amount: z.number().finite(),
        tax: z.number().finite(),
        totalAmount: z.number().finite(),
        currency: z.string().trim().length(3).nullable(),
        createDate: coreTimestampSchema.nullable(),
        orderId: z.string().uuid().nullable(),
    })),
    orders: z.array(z.object({
        id: z.string().uuid(),
        code: z.string().trim().nullable(),
        title: z.string().trim().nullable(),
        status: z.number().int(),
        total: z.number().finite(),
        currency: z.string().trim().length(3).nullable(),
        createDate: coreTimestampSchema.nullable(),
        workspace: z.string().trim().nullable(),
    })),
});
const httpUrlSchema = z
    .string()
    .url()
    .max(2_048)
    .refine((value) => {
    const protocol = new URL(value).protocol;
    return protocol === "http:" || protocol === "https:";
}, "URL must use HTTP or HTTPS");
export const checkoutInputSchema = z.object({
    planId: z.string().trim().min(1).max(100),
    productVariantId: z.string().trim().min(1).max(200).optional(),
    billingPeriod: billingPeriodSchema,
    successUrl: httpUrlSchema,
    cancelUrl: httpUrlSchema,
});
export const checkoutResponseSchema = z.object({
    checkoutUrl: httpUrlSchema,
});
export const checkoutStatusSchema = z.object({
    order: z
        .object({
        id: z.string().min(1),
        status: z.number().int(),
        totalPrice: z.union([z.string(), z.number()]),
        currency: z.object({ code: z.string().length(3) }).optional(),
        subscriptionPlan_id: z.string().min(1).nullable().optional(),
        lastUpdateDate: coreTimestampSchema.optional(),
    })
        .nullable(),
    subscription: z.unknown().nullable(),
    error: z.string().nullable().optional(),
});
export const subscriptionMutationSchema = z.object({
    subscriptionId: z.string().trim().min(1).max(200),
});
export const planChangeInputSchema = z.object({
    planId: z.string().trim().min(1).max(100),
    billingPeriod: billingPeriodSchema,
});
export const planChangeResultSchema = z.object({
    changeType: z.enum(["none", "upgrade", "downgrade"]),
    chargeAmount: z.union([z.string(), z.number()]).optional(),
    currency: z.string().optional(),
});
export const billingFeatureConfigSchema = z.object({
    pricingPath: z.string().startsWith("/").default("/pricing"),
    registrationPath: z.string().startsWith("/").default("/auth/register"),
    subscriptionPath: z.string().startsWith("/").default("/app/subscriptions"),
    checkoutSuccessPath: z.string().startsWith("/").default("/checkout/success"),
    defaultContinuePath: z.string().startsWith("/").default("/app/dashboard"),
    provider: billingProviderSchema.optional(),
    plans: billingPlansSchema.default([
        {
            id: "free",
            name: "Free",
            description: "Core features for getting started",
            currency: "USD",
            prices: { monthly: { amount: 0 }, yearly: { amount: 0 } },
            features: ["Core workspace"],
            popular: false,
        },
        {
            id: "premium",
            name: "Premium",
            description: "Expanded limits and premium features",
            currency: "USD",
            prices: { monthly: { amount: 12 }, yearly: { amount: 120 } },
            features: ["Expanded usage", "Priority support"],
            popular: true,
        },
    ]),
});
//# sourceMappingURL=contracts.js.map